// =============================================================================
// pages/PazarAnalizi.js — Pazar Analizi DETAYLI (Faz E v2.0)
// =============================================================================
// 50+ veri noktası — Türkiye plastik ambalaj sektörü kapsamlı raporu.
// Kaynaklar: PAGEV, TÜİK, ASD, PLASFED, T.C. Ticaret/Çevre/Kültür Bakanlıkları,
// ChemOrbis, ETBİS, Mordor Intelligence, Şikayetvar, Ekşi, Avansas, Adroket.
// Veri kesim tarihi: Mayıs 2026.
// =============================================================================

import React from "react";
import {
  Box, Typography, Grid, Card, CardContent, Stack, Chip,
  Tabs, Tab, Paper, LinearProgress, Divider, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import StoreIcon from "@mui/icons-material/Store";
import GroupsIcon from "@mui/icons-material/Groups";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FactoryIcon from "@mui/icons-material/Factory";
import EcoIcon from "@mui/icons-material/Park";
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket";

// ─── 8 ANA KPI ──────────────────────────────────────────────────────────────
const PAZAR_KPI = [
  { label: "Plastik Ambalaj Cirosu", value: "15,04 Mlr USD", sub: "PAGEV 2025", color: "#1565C0" },
  { label: "Plastik Mamul Üretim", value: "10+ Mn ton", sub: "Toplam plastik 2025", color: "#0d4a8e" },
  { label: "Plastik Ambalaj Üretim", value: "4,16 Mn ton", sub: "Sektör alt-segmenti", color: "#3b82f6" },
  { label: "Yıllık Büyüme (USD)", value: "%8,8", sub: "CAGR 2024-25", color: "#10b981" },
  { label: "Sektörel Konum", value: "Avrupa 2.", sub: "Dünya 6.", color: "#E67E22" },
  { label: "İhracat", value: "9,57 Mlr USD", sub: "Plastik mamul 2025", color: "#7c3aed" },
  { label: "Dış Ticaret Fazlası", value: "+930 Mn USD", sub: "Plastik ambalaj H1'25", color: "#f59e0b" },
  { label: "Sektör İstihdamı", value: "~250.000", sub: "Çalışan sayısı", color: "#ef4444" }
];

// ─── ALT-KATEGORI DAĞILIMI ──────────────────────────────────────────────────
const ALT_KATEGORI = [
  { name: "Esnek (film/poşet/torba)", pay: 50, ihracat: "1,89 Mlr USD" },
  { name: "Sert (şişe/kap/kapak)", pay: 33, ihracat: "≈" },
  { name: "Streç & Shrink Film", pay: 10, ihracat: "—" },
  { name: "Diğer (kapak, dispenser)", pay: 7, ihracat: "—" }
];

// ─── BÖLGESEL ÜRETIM ────────────────────────────────────────────────────────
const BOLGESEL = [
  { bolge: "Marmara (İstanbul + Tekirdağ + Çorlu + Bursa + Kocaeli)", pay: 62 },
  { bolge: "Ege (İzmir + Manisa + Aydın)", pay: 12 },
  { bolge: "İç Anadolu (Ankara + Konya + Kayseri)", pay: 10 },
  { bolge: "Akdeniz (Adana + Mersin + Antalya)", pay: 6 },
  { bolge: "Güneydoğu Anadolu (Gaziantep)", pay: 5 },
  { bolge: "Karadeniz", pay: 3 },
  { bolge: "Doğu Anadolu", pay: 2 }
];

// ─── HAMMADDE FİYATLARI (Mart-Mayıs 2026) ──────────────────────────────────
const HAMMADDE = [
  { name: "HDPE", price: "950–1.150 USD/t", trend: "↑", source: "ChemOrbis Q1'26" },
  { name: "LDPE/LLDPE", price: "1.000–1.200 USD/t", trend: "↑", source: "ICIS / Petkim" },
  { name: "PP (Polipropilen)", price: "1.150–1.250 USD/t", trend: "↑↑", source: "ChemOrbis Şub'26" },
  { name: "PS (GPPS/HIPS)", price: "1.200–1.350 USD/t", trend: "→", source: "Plastics Tech" },
  { name: "PVC", price: "850–1.000 USD/t", trend: "↑↑", source: "ChemOrbis Mart'26" },
  { name: "PET (şişe-grade)", price: "950–1.100 USD/t", trend: "↑", source: "ChemOrbis" }
];

// ─── KULLANIM SEGMENTLERİ ──────────────────────────────────────────────────
const KULLANIM_SEGMENT = [
  { segment: "Gıda & İçecek", pay: 53, color: "#10b981", desc: "Et/süt/kuru gıda/içecek şişe — sektörün #1 alıcısı" },
  { segment: "İlaç & Medikal", pay: 18, color: "#7c3aed", desc: "İlaç ambalaj %60'ı plastik (şişe/blister)" },
  { segment: "Endüstriyel", pay: 12, color: "#1565C0", desc: "Streç film, IBC, varil, palet örtü" },
  { segment: "Ev Temizlik & Kozmetik", pay: 8, color: "#E67E22", desc: "Sprey şişe, bidon, tüp, dispenser" },
  { segment: "E-Ticaret Kargo", pay: 5, color: "#3b82f6", desc: "Köpük, balon, streç, koruyucu — hızlı büyüyor" },
  { segment: "Tarım", pay: 4, color: "#84cc16", desc: "Sera örtü filmi, çuval, mulch film" }
];

// ─── E-TİCARET ─────────────────────────────────────────────────────────────
const ETICARET = [
  { label: "Toplam E-Ticaret 2024", value: "3 Trilyon TL", sub: "~52 Mlr USD, dünyada 12." },
  { label: "Yıllık Büyüme 23→24", value: "+%34", sub: "1,38 → 3 trilyon TL" },
  { label: "Online Alışveriş Yapan", value: "48,6 milyon", sub: "ETBİS 2025" },
  { label: "Perakende İçi Pay", value: "%21", sub: "ETBİS 2025" },
  { label: "Günlük Kargo Kabulü", value: "1.000.000+", sub: "Tarihi rekor 2025" },
  { label: "Yıllık Kargo Hacmi", value: "~365 Mn paket", sub: "Türetilmiş" },
  { label: "Trendyol Ciro", value: "150–200 Mlr TL", sub: "2025 tahmin" },
  { label: "Hepsiburada Ciro", value: "69 Mlr TL", sub: "2025 KAP" }
];

// ─── HORECA ────────────────────────────────────────────────────────────────
const HORECA = [
  { label: "Restoran + Kafe + Kahve dükkanı", value: "150.000+", source: "Dünya Food / OEM HORECA 2024" },
  { label: "Kayıtlı Restoran (TÜİK)", value: "~70.000", source: "TÜİK 2023-24" },
  { label: "Bakanlık Belgeli Konaklama", value: "22.091 tesis", source: "T.C. KTB Şubat 2025" },
  { label: "Toplam Yatak Kapasitesi", value: "1.982.919", source: "KTB" },
  { label: "Toplam Oda Sayısı", value: "952.982", source: "KTB" },
  { label: "Foodservice Pazarı", value: "14,72 Mlr USD", source: "Mordor Intelligence 2025" },
  { label: "2030 Foodservice Tahmini", value: "27,09 Mlr USD", source: "Mordor" },
  { label: "HORECA CAGR 2025-2030", value: "+%13/yıl", source: "Mordor" }
];

// ─── REKLAM MALİYETLERİ 2026 ───────────────────────────────────────────────
const REKLAM_MALIYET = [
  { kanal: "Google Ads (genel)", deger: "3-25 TL/tık", source: "212 Medya 2026" },
  { kanal: "Google Ads B2B/teknoloji", deger: "30-140 TL/tık", source: "212 Medya — karar verici" },
  { kanal: "Aylık Google Ads bütçe", deger: "3K-50K TL", source: "Sektör genel" },
  { kanal: "Meta Ads CPM", deger: "35-120 TL/1000", source: "Adroket / Inside Media" },
  { kanal: "Meta Ads CPC (B2B)", deger: "2-9 TL", source: "Adroket — rekabetçi 20+ TL" },
  { kanal: "LinkedIn B2B CPC", deger: "5-15 TL", source: "Adroket / Stackmatix" },
  { kanal: "LinkedIn günlük bütçe", deger: "200-400 TL/gün", source: "Adroket" }
];

// ─── SUP REGÜLASYON ────────────────────────────────────────────────────────
const SUP_REGULASYON = [
  { label: "Yasak Başlangıç", value: "1 Eylül 2026", desc: "Kademeli; kalan kategoriler Ocak 2027" },
  { label: "Etkilenen Pazar", value: "~1,5 Mlr TL/yıl", desc: "Tek kullanımlık tasarrufu" },
  { label: "CO2 Azaltımı", value: "1,5 Mn ton/yıl", desc: "CO2-eşdeğer" },
  { label: "Biyobozunur Maliyet", value: "2-3x artış", desc: "Kağıt pipet 5-7x" },
  { label: "Türkiye Geri Dönüşüm", value: "1,1 Mn ton/yıl", desc: "AA / Çevre Bakanlığı 2024" },
  { label: "Genel Atık Geri Kazanım", value: "%13 → %22,4", desc: "2017 → 2024 (Sıfır Atık)" },
  { label: "DYS Hedefi", value: "25 Mlr ambalaj/yıl", desc: "Depozito sistemi" },
  { label: "DYS Otomatik Makine", value: "5.000 adet", desc: "Faz 1 ülke geneli" }
];

// ─── ŞİKAYETVAR ────────────────────────────────────────────────────────────
const SIKAYETVAR_VERI = [
  { label: "Toplam şikayet (tüm sektörler)", value: "2.868.914", source: "Şikayetvar Şeffaflık 2025" },
  { label: "E-ticaret şikayeti", value: "365.395", source: "Şikayetvar 2025" },
  { label: "Kargo & nakliyat", value: "141.851", source: "Şikayetvar 2025" }
];

// ─── MEMNUNİYET / ŞİKAYET ──────────────────────────────────────────────────
const MEMNUNIYET = [
  { label: "Hızlı/aynı gün teslimat", weight: 92 },
  { label: "Stok sürekliliği + multi-SKU", weight: 88 },
  { label: "Şeffaf fiyat + hacim iskontosu", weight: 85 },
  { label: "Kalite tutarlılığı (mikron/gramaj)", weight: 84 },
  { label: "Numune + küçük ilk sipariş", weight: 76 }
];

const SIKAYET = [
  { label: "Geç teslim ve oyalama", weight: 95, severity: "yüksek" },
  { label: "Kalite düşüklüğü (yırtık poşet)", weight: 88, severity: "yüksek" },
  { label: "Hatalı/yanlış ürün gönderimi", weight: 76, severity: "orta" },
  { label: "İade kargosu pahalı", weight: 65, severity: "orta" },
  { label: "Müşteri hizmeti yanıtsızlığı", weight: 80, severity: "yüksek" }
];

// ─── RAKİPLER (genişletildi) ───────────────────────────────────────────────
const RAKIPLER = [
  { name: "Korozo / Koroplast", konum: "İstanbul", profil: "Türkiye'nin en büyük 3'ünden", durum: "Kalite + yanıtsızlık şikayetleri yoğun", risk: "yüksek" },
  { name: "Naksan Plastik (Kimpack)", konum: "Gaziantep", profil: "280K ton/yıl, kurumsal", durum: "B2B endüstriyel", risk: "orta" },
  { name: "Avansas", konum: "Online", profil: "5K+ SKU, KOBİ ofis-sarf lideri", durum: "Palet/vade yapmıyor — Mik-San fırsatı", risk: "düşük (B2B)" },
  { name: "Polmix / Polsan", konum: "İstanbul", profil: "200 ton/ay multi-kategori", durum: "Düşük şikayet/görünürlük", risk: "düşük" },
  { name: "Ambalaj Store/Sepete", konum: "Online", profil: "E-ticaret toptancı", durum: "Teslim gecikme şikayetleri", risk: "orta" },
  { name: "Tepsan / Öztaş / Traplast", konum: "Tekirdağ", profil: "Yerel üretici", durum: "Çoklu marka satmıyor", risk: "orta" },
  { name: "Yakutsan Ambalaj", konum: "İstanbul", profil: "Polmix dağıtıcısı multi-marka", durum: "Mik-San benzeri model", risk: "orta" },
  { name: "Bizim Toptan", konum: "Ulusal", profil: "Gıda ağırlıklı, Parex markası", durum: "Tek-stop değil", risk: "düşük" }
];

const FIYAT = [
  { sku: "Çöp poşeti orta boy 100'lü", min: 130, ort: 320, max: 518 },
  { sku: "Çöp poşeti büyük 65x80, 50 rulo", min: 199, ort: 300, max: 400 },
  { sku: "Streç 17 mikron 50x300, 6'lı koli", min: 1410, ort: 1700, max: 2399 },
  { sku: "Lateks pudralı 100'lü kutu", min: 175, ort: 245, max: 309 },
  { sku: "Nitril pudrasız 100'lü kutu", min: 75, ort: 235, max: 349 },
  { sku: "Market poşeti kalsitli (kg)", min: 14, ort: 18, max: 30 }
];

const SEGMENT_AYLIK = [
  { segment: "HORECA (otel/restoran/kafe)", aylikAlim: "5K–50K ₺", karar: "Sahip / şef / satınalma", LTV: "orta-yüksek" },
  { segment: "Marketler (yerel + zincir)", aylikAlim: "50K–500K ₺", karar: "Kategori müdürü", LTV: "yüksek" },
  { segment: "Üretim/Fabrika", aylikAlim: "100K–2M ₺", karar: "Lojistik müdürü", LTV: "çok yüksek" },
  { segment: "Hastane/Medikal", aylikAlim: "30K–200K ₺", karar: "Başhemşire", LTV: "yüksek" },
  { segment: "OSB tesisleri", aylikAlim: "20K–500K ₺", karar: "İdari işler", LTV: "yüksek" },
  { segment: "E-ticaret kargo", aylikAlim: "10K–300K ₺", karar: "Operasyon", LTV: "orta-yüksek" },
  { segment: "Belediye/Kamu (EKAP)", aylikAlim: "200K–5M ₺/yıl", karar: "İhale komisyonu", LTV: "çok yüksek" }
];

// ─── STRATEJİK FIRSATLAR (genişletildi) ────────────────────────────────────
const STRATEJIK_FIRSATLAR = [
  { baslik: "Söz verdiğin günde teslim", aciklama: "Sektörün #1 şikayetine direkt çözüm. 24h SLA Marmara için verilebilir.", oncelik: "kritik" },
  { baslik: "Şikayetvar marka profili açık tut", aciklama: "Koroplast/Korozo'nun kapanık tutumu fırsat. Şeffaf yanıt rakipsiz farklılaşma.", oncelik: "yüksek" },
  { baslik: "Mikron/gramaj etiketi göster", aciklama: "Kalite şikayetlerinin yarısı 'vaadedilen kalınlık tutmuyor'.", oncelik: "yüksek" },
  { baslik: "Hacim iskonto tablosu yayınla", aciklama: "Şeffaflık avantajı, 'sürpriz fatura yok' sözüne pozitif tepki.", oncelik: "orta" },
  { baslik: "Restoran/OSB için aylık abonelik", aciklama: "Mik-San'ın irsaliye otomasyonu farklılaşma noktası — abonelik = sürekli nakit.", oncelik: "yüksek" },
  { baslik: "Biyobozunur seri (SUP yasağı)", aciklama: "1 Eylül 2026 yasağı sonrası 2-3x maliyet artışı. Erken pozisyon alan kazanır.", oncelik: "kritik" },
  { baslik: "E-ticaret kargo paketleme paketi", aciklama: "Günde 1M+ kargo. 'Trendyol/Hepsiburada satıcı paketi' = 5 ürün bundle.", oncelik: "yüksek" },
  { baslik: "EKAP belediye/hastane ihale takibi", aciklama: "Yıllık 200K-5M ₺ ihale fırsatı. Otomasyon zaten hazır.", oncelik: "yüksek" }
];

const MIKSAN_REKABET = [
  { metric: "Marmara plastik ambalaj toptancı", value: "~1.000", desc: "1.450 ambalaj firmasının %70'i Marmara" },
  { metric: "325+ ürün katalog ciro etkisi", value: "+%15-25", desc: "Genişletilmiş katalog sepet etkisi (McKinsey B2B)" },
  { metric: "24h teslim sözüyle dönüşüm", value: "+%15-30", desc: "B2B express delivery genel benchmark" },
  { metric: "Otomatik fatura penetrasyonu", value: "<%30", desc: "KOBİ toptancılar — Mik-San diğer %70'i sollar" },
  { metric: "B2B vade çalışan toptancı", value: "%70-85", desc: "Türkiye KOBİ ödeme normu" },
  { metric: "Sektör B2B karar süresi", value: "~2 ay", desc: "ABD benchmark — Türkiye'de benzer" },
  { metric: "AI/üretken yapay zeka kullanımı", value: "%87", desc: "B2B alıcıları satın alma sürecinde" }
];

// ============================================================================
// COMPONENT
// ============================================================================

function Bar({ value, color = "primary", height = 8 }) {
  return <LinearProgress variant="determinate" value={Math.min(100, value)} color={color} sx={{ height, borderRadius: height/2 }} />;
}

export default function PazarAnalizi() {
  const [tab, setTab] = React.useState(0);

  return (
    <Box sx={{ p: 2 }}>
      {/* HEADER */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: "#1565C0" }}>
            📊 Pazar Analizi — Detaylı Sektör Raporu
          </Typography>
          <Typography variant="caption" color="text.secondary">
            PAGEV · TÜİK · ASD · Bakanlık · ChemOrbis · ETBİS · Mordor · Şikayetvar — 50+ veri noktası
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Chip label="Mayıs 2026" color="primary" size="small" />
          <Chip label="V2.0 Detaylı" size="small" variant="outlined" />
        </Stack>
      </Stack>

      {/* 8 ANA KPI */}
      <Grid container spacing={2} mb={3}>
        {PAZAR_KPI.map((k, i) => (
          <Grid item xs={6} sm={4} md={3} key={i}>
            <Card sx={{ borderTop: `4px solid ${k.color}` }}>
              <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                <Typography variant="caption" color="text.secondary">{k.label}</Typography>
                <Typography variant="h6" fontWeight={800} sx={{ color: k.color, my: 0.5 }}>{k.value}</Typography>
                <Typography variant="caption" color="text.disabled">{k.sub}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* TABS */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab icon={<TrendingUpIcon />} iconPosition="start" label="Müşteri Davranışı" />
          <Tab icon={<FactoryIcon />} iconPosition="start" label="Sektör Detayı" />
          <Tab icon={<ShoppingBasketIcon />} iconPosition="start" label="Müşteri Pazarı" />
          <Tab icon={<EcoIcon />} iconPosition="start" label="Sürdürülebilirlik" />
          <Tab icon={<StoreIcon />} iconPosition="start" label="Rakipler" />
          <Tab icon={<GroupsIcon />} iconPosition="start" label="Segmentler" />
          <Tab icon={<LightbulbIcon />} iconPosition="start" label="Stratejik Fırsatlar" />
        </Tabs>
      </Paper>

      {/* TAB 0: MÜŞTERİ DAVRANIŞI */}
      {tab === 0 && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  <CheckCircleIcon color="success" />
                  <Typography variant="h6" fontWeight={700}>Müşteri Memnuniyet Temaları</Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">Şikayetvar + Ekşi + Google Reviews</Typography>
                {MEMNUNIYET.map((m, i) => (
                  <Box key={i} mt={2}>
                    <Stack direction="row" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2">{m.label}</Typography>
                      <Typography variant="caption" color="success.main" fontWeight={700}>{m.weight}%</Typography>
                    </Stack>
                    <Bar value={m.weight} color="success" height={6} />
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  <WarningIcon color="error" />
                  <Typography variant="h6" fontWeight={700}>Müşteri Şikayet Temaları</Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">Sektör acı noktaları — Mik-San fırsat</Typography>
                {SIKAYET.map((s, i) => (
                  <Box key={i} mt={2}>
                    <Stack direction="row" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2">{s.label}</Typography>
                      <Chip label={s.severity} size="small" color={s.severity === 'yüksek' ? 'error' : 'warning'} />
                    </Stack>
                    <Bar value={s.weight} color="error" height={6} />
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700} mb={2}>💰 Toptan Fiyat Aralıkları (TL, Mayıs 2026)</Typography>
                <Grid container spacing={1}>
                  {FIYAT.map((f, i) => (
                    <Grid item xs={12} md={6} key={i}>
                      <Box sx={{ p: 1.5, border: "1px solid #e2e8f0", borderRadius: 2, mb: 1 }}>
                        <Typography variant="body2" fontWeight={600}>{f.sku}</Typography>
                        <Stack direction="row" spacing={2} mt={1}>
                          <Box flex={1}><Typography variant="caption">Min</Typography><Typography variant="body1" fontWeight={700} color="success.main">{f.min} ₺</Typography></Box>
                          <Box flex={1}><Typography variant="caption">Ort.</Typography><Typography variant="body1" fontWeight={700} color="primary.main">{f.ort} ₺</Typography></Box>
                          <Box flex={1}><Typography variant="caption">Max</Typography><Typography variant="body1" fontWeight={700} color="error.main">{f.max} ₺</Typography></Box>
                        </Stack>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                <Alert severity="info" sx={{ mt: 2, fontSize: 12 }}>
                  Kaynak: Akakçe + Avansas + Beybi + Teknopak + Sarkap + Ambalaj Store + Mert Pazarlama
                </Alert>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700} mb={2}>📞 Şikayetvar Şeffaflık Raporu 2025</Typography>
                <Grid container spacing={2}>
                  {SIKAYETVAR_VERI.map((s, i) => (
                    <Grid item xs={12} md={4} key={i}>
                      <Box sx={{ textAlign: 'center', p: 2, border: '1px solid #e2e8f0', borderRadius: 2 }}>
                        <Typography variant="h4" fontWeight={800} color="error.main">{s.value}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>{s.label}</Typography>
                        <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>{s.source}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* TAB 1: SEKTÖR DETAYI */}
      {tab === 1 && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700} mb={2}>📦 Plastik Ambalaj Alt-Kategori</Typography>
                {ALT_KATEGORI.map((k, i) => (
                  <Box key={i} mb={2}>
                    <Stack direction="row" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2">{k.name}</Typography>
                      <Typography variant="body2" fontWeight={700}>%{k.pay}</Typography>
                    </Stack>
                    <Bar value={k.pay * 1.5} />
                    <Typography variant="caption" color="text.disabled">İhracat: {k.ihracat}</Typography>
                  </Box>
                ))}
                <Alert severity="info" sx={{ mt: 1, fontSize: 11 }}>PAGEV 2025 + ihracat HS kodu</Alert>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700} mb={2}>🗺️ Bölgesel Üretim Dağılımı</Typography>
                {BOLGESEL.map((b, i) => (
                  <Box key={i} mb={1.5}>
                    <Stack direction="row" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2" sx={{ fontSize: 13 }}>{b.bolge}</Typography>
                      <Typography variant="body2" fontWeight={700} color="primary.main">%{b.pay}</Typography>
                    </Stack>
                    <Bar value={b.pay * 1.5} height={6} />
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700} mb={2}>⛽ Hammadde Fiyatları (Mart-Mayıs 2026)</Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow><TableCell>Hammadde</TableCell><TableCell>Fiyat</TableCell><TableCell>Trend</TableCell><TableCell>Kaynak</TableCell></TableRow>
                    </TableHead>
                    <TableBody>
                      {HAMMADDE.map((h, i) => (
                        <TableRow key={i} hover>
                          <TableCell><strong>{h.name}</strong></TableCell>
                          <TableCell>{h.price}</TableCell>
                          <TableCell><Chip label={h.trend} size="small" color={h.trend.includes('↑↑') ? 'error' : h.trend.includes('↑') ? 'warning' : 'default'} /></TableCell>
                          <TableCell sx={{ fontSize: 11 }}>{h.source}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Alert severity="warning" sx={{ mt: 2, fontSize: 12 }}>
                  Türkiye polyolefin pazarı çok-yıllık zirvelerde — Orta Doğu/ABD/Asya arz kısıtı + jeopolitik gerilimler.
                </Alert>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700} mb={2}>🥡 Plastik Ambalaj Kullanım Segmentleri</Typography>
                {KULLANIM_SEGMENT.map((s, i) => (
                  <Box key={i} mb={2}>
                    <Stack direction="row" justifyContent="space-between" mb={0.5}>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{s.segment}</Typography>
                        <Typography variant="caption" color="text.secondary">{s.desc}</Typography>
                      </Box>
                      <Typography variant="h6" fontWeight={800} sx={{ color: s.color }}>%{s.pay}</Typography>
                    </Stack>
                    <LinearProgress variant="determinate" value={s.pay * 1.5}
                      sx={{ height: 6, borderRadius: 3, '& .MuiLinearProgress-bar': { bgcolor: s.color } }} />
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* TAB 2: MÜŞTERİ PAZARI */}
      {tab === 2 && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700} mb={2}>🛒 Türkiye E-Ticaret Pazarı</Typography>
                <Grid container spacing={1.5}>
                  {ETICARET.map((e, i) => (
                    <Grid item xs={6} md={3} key={i}>
                      <Box sx={{ p: 1.5, textAlign: 'center', border: '1px solid #e2e8f0', borderRadius: 2 }}>
                        <Typography variant="h6" fontWeight={800} color="primary.main">{e.value}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">{e.label}</Typography>
                        <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>{e.sub}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                <Alert severity="success" sx={{ mt: 2, fontSize: 12 }}>
                  <strong>Mik-San fırsatı:</strong> Günlük 1M+ kargo = sürekli kraft koli + balonlu naylon + streç + bant talebi. Gıda kategorisi %52 büyüyor.
                </Alert>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700} mb={2}>🍽️ HORECA Pazarı (Otel + Restoran + Kafe)</Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead><TableRow><TableCell>Gösterge</TableCell><TableCell>Değer</TableCell><TableCell>Kaynak</TableCell></TableRow></TableHead>
                    <TableBody>
                      {HORECA.map((h, i) => (
                        <TableRow key={i} hover>
                          <TableCell>{h.label}</TableCell>
                          <TableCell><strong>{h.value}</strong></TableCell>
                          <TableCell sx={{ fontSize: 11 }}>{h.source}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Alert severity="success" sx={{ mt: 2, fontSize: 12 }}>
                  <strong>Mik-San fırsatı:</strong> 150K+ HORECA — eldiven, streç, alüminyum, çöp poşeti, peçete sürekli talep. Foodservice +%13/yıl.
                </Alert>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700} mb={2}>📢 Reklam Maliyetleri 2026 (Türkiye)</Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead><TableRow><TableCell>Kanal</TableCell><TableCell>Birim Fiyat</TableCell><TableCell>Kaynak</TableCell></TableRow></TableHead>
                    <TableBody>
                      {REKLAM_MALIYET.map((r, i) => (
                        <TableRow key={i} hover>
                          <TableCell><strong>{r.kanal}</strong></TableCell>
                          <TableCell><Chip label={r.deger} size="small" color="primary" variant="outlined" /></TableCell>
                          <TableCell sx={{ fontSize: 11 }}>{r.source}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* TAB 3: SÜRDÜRÜLEBİLİRLİK */}
      {tab === 3 && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>1 Eylül 2026 Tek Kullanımlık Plastik (SUP) Yasağı</strong> — biyobozunur muadil 2-3x maliyet artışı,
              kağıt pipet 5-7x. Erken pozisyon alan tedarikçi pazarı yakalar.
            </Alert>
          </Grid>
          {SUP_REGULASYON.map((s, i) => (
            <Grid item xs={6} md={3} key={i}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                  <Typography variant="h6" fontWeight={800} sx={{ color: '#16a34a', my: 0.5 }}>{s.value}</Typography>
                  <Typography variant="caption" color="text.disabled">{s.desc}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700} mb={2}>🌍 AB PPWR Etkisi (Türk İhracatçılarına)</Typography>
                <Typography variant="body2" mb={1}>
                  Avrupa Plastik Ambalaj Yönetmeliği (PPWR) 2030/2040 hedefleriyle <strong>geri dönüştürülmüş içerik %10-35 zorunluluğu</strong>.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Plastik ambalaj ihracatı 2025 H1: <strong>2,1 Mlr USD</strong> — büyük kısmı AB pazarı<br/>
                  • Sertifikalı geri dönüştürülmüş içerik üretim kapasitesi Türkiye'de sınırlı<br/>
                  • <strong>Fırsat:</strong> r-PE, r-PET sertifikalı seri yatırımı = dış ticaret fazlasını koruma
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* TAB 4: RAKİPLER */}
      {tab === 4 && (
        <Grid container spacing={2}>
          {RAKIPLER.map((r, i) => (
            <Grid item xs={12} md={6} key={i}>
              <Card>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Box>
                      <Typography variant="h6" fontWeight={700} sx={{ color: "#1a3d6d" }}>{r.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{r.konum}</Typography>
                    </Box>
                    <Chip label={r.risk} color={r.risk.includes("yüksek") ? "error" : r.risk.includes("orta") ? "warning" : "default"} size="small" />
                  </Stack>
                  <Typography variant="body2" mb={1}><strong>Profil:</strong> {r.profil}</Typography>
                  <Typography variant="body2" color="text.secondary"><strong>Durum:</strong> {r.durum}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* TAB 5: SEGMENTLER */}
      {tab === 5 && (
        <Grid container spacing={2}>
          {SEGMENT_AYLIK.map((s, i) => (
            <Grid item xs={12} md={6} key={i}>
              <Card>
                <CardContent>
                  <Typography variant="h6" fontWeight={700} sx={{ color: "#1565C0" }}>{s.segment}</Typography>
                  <Divider sx={{ my: 1 }} />
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between"><Typography variant="body2" color="text.secondary">Aylık alım:</Typography><Typography variant="body2" fontWeight={700}>{s.aylikAlim}</Typography></Stack>
                    <Stack direction="row" justifyContent="space-between"><Typography variant="body2" color="text.secondary">Karar verici:</Typography><Typography variant="body2">{s.karar}</Typography></Stack>
                    <Stack direction="row" justifyContent="space-between"><Typography variant="body2" color="text.secondary">LTV:</Typography><Chip label={s.LTV} size="small" color={s.LTV.includes("çok") ? "success" : s.LTV.includes("yüksek") ? "primary" : "default"} /></Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* TAB 6: STRATEJİK FIRSATLAR + MİK-SAN REKABET POZİSYONU */}
      {tab === 6 && (
        <Grid container spacing={2}>
          {STRATEJIK_FIRSATLAR.map((f, i) => (
            <Grid item xs={12} md={6} key={i}>
              <Card sx={{ borderLeft: `4px solid ${f.oncelik === 'kritik' ? '#ef4444' : f.oncelik === 'yüksek' ? '#1565C0' : '#94a3b8'}`, height: '100%' }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" mb={1}>
                    <Typography variant="h6" fontWeight={700}>💡 {f.baslik}</Typography>
                    <Chip label={f.oncelik} size="small" color={f.oncelik === 'kritik' ? 'error' : f.oncelik === 'yüksek' ? 'primary' : 'default'} />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">{f.aciklama}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700} mb={2}>🎯 Mik-San Rekabet Pozisyonu (Sayısal)</Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead><TableRow><TableCell>Metrik</TableCell><TableCell>Değer</TableCell><TableCell>Açıklama</TableCell></TableRow></TableHead>
                    <TableBody>
                      {MIKSAN_REKABET.map((m, i) => (
                        <TableRow key={i} hover>
                          <TableCell><strong>{m.metric}</strong></TableCell>
                          <TableCell><Chip label={m.value} size="small" color="primary" /></TableCell>
                          <TableCell sx={{ fontSize: 11 }}>{m.desc}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Alert severity="success" icon={<LightbulbIcon />}>
              <strong>Mik-San USP'leri:</strong> Renksan yetkili bayii (fabrika fiyatı) · 24h Marmara teslim ·
              Otomatik dijital irsaliye/fatura · 325+ kalem stok · Açık cari + 30/60/90 vade ·
              Sektör otomasyon penetrasyonu &lt;%30 → fark yaratıcı pozisyon
            </Alert>
          </Grid>
        </Grid>
      )}

      {/* FOOTER — kaynaklar */}
      <Card sx={{ mt: 3, bgcolor: '#f8fafc' }}>
        <CardContent>
          <Typography variant="caption" color="text.secondary">
            <strong>Kaynaklar:</strong> PAGEV 2025 İzleme Raporları · TÜİK NACE 22.22 · T.C. Ticaret Bakanlığı ETBİS ·
            T.C. Çevre, Şehircilik ve İklim Değişikliği Bakanlığı · T.C. Kültür ve Turizm Bakanlığı (KTB) ·
            Mordor Intelligence Foodservice · ChemOrbis &amp; Plastics Technology · Şikayetvar Şeffaflık 2025 ·
            ASD/PLASFED/SEPA · Adroket/212 Medya · Ekşi + Google Reviews. "Veri yok" işaretli kalemler kamuya açık değil.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
